import React, { useState, useEffect } from 'react';
import { ExternalLink, AlertTriangle, Activity, Globe, Lock, BarChart, Zap, Server, Eye, Bell, Terminal, LineChart, Cpu, Layers, ChevronRight, ShieldAlert, Network } from 'lucide-react';

function Dashboard() {
  // Real-time state management for various dashboard components
  const [pulseRate, setPulseRate] = useState(1000);
  const [globalThreatLevel, setGlobalThreatLevel] = useState(75);
  const [threatStatus, setThreatStatus] = useState('ELEVATED');
  
  // Node result states
  const [nodeResults, setNodeResults] = useState([
    { id: 1, health: 'Healthy', performance: 98.7, status: 'Performance Optimal' },
    { id: 2, health: 'Healthy', performance: 99.1, status: 'Performance Optimal' },
    { id: 3, health: 'Healthy', performance: 97.4, status: 'Performance Optimal' }
  ]);
  
  // Indexer logs status
  const [indexerStats, setIndexerStats] = useState({
    logsProcessed: 1400000,
    indexingRate: 12700,
    latency: 124,
    uptime: 99.9
  });
  
  // Master node stats
  const [masterNodeStats, setMasterNodeStats] = useState({
    cpu: 12,
    memory: 3.8,
    load: 0.23
  });
  
  // Worker nodes stats
  const [workerNodes, setWorkerNodes] = useState([
    { id: 1, name: 'AlertStrike-Worker-1', ip: 'Demo IP', status: 'Healthy', cpu: 23, memory: 4.2, tasks: 42 },
    { id: 2, name: 'AlertStrike-Worker-2', ip: 'Demo IP', status: 'Healthy', cpu: 18, memory: 3.9, tasks: 38 },
    { id: 3, name: 'AlertStrike-Worker-3', ip: 'Demo IP', status: 'Healthy', cpu: 36, memory: 3.1, tasks: 49 },
    { id: 4, name: 'AlertStrike-Worker-4', ip: 'Demo IP', status: 'Healthy', cpu: 21, memory: 4.0, tasks: 41 },
    { id: 5, name: 'AlertStrike-Worker-5', ip: 'Demo IP', status: 'Healthy', cpu: 19, memory: 3.7, tasks: 37 }
  ]);

  // Update real-time data at intervals
  useEffect(() => {
    // Update global threat level
    const threatInterval = setInterval(() => {
      const newThreatLevel = Math.floor(65 + Math.random() * 25);
      setGlobalThreatLevel(newThreatLevel);
      
      if (newThreatLevel > 85) {
        setThreatStatus('CRITICAL');
      } else if (newThreatLevel > 70) {
        setThreatStatus('ELEVATED');
      } else {
        setThreatStatus('MODERATE');
      }
    }, 5000);
    
    // Update node results
    const nodeInterval = setInterval(() => {
      setNodeResults(prev => prev.map(node => {
        const performanceChange = (Math.random() > 0.5 ? 1 : -1) * (Math.random() * 0.5);
        const newPerformance = Math.min(Math.max(node.performance + performanceChange, 95), 99.5);
        
        return {
          ...node,
          performance: parseFloat(newPerformance.toFixed(1)),
          status: newPerformance > 98 ? 'Performance Optimal' : 'Performance Good'
        };
      }));
    }, 3000);
    
    // Update indexer logs
    const indexerInterval = setInterval(() => {
      setIndexerStats(prev => ({
        logsProcessed: prev.logsProcessed + Math.floor(Math.random() * 10000),
        indexingRate: 12500 + Math.floor(Math.random() * 500),
        latency: 120 + Math.floor(Math.random() * 10),
        uptime: Math.min(99.99, prev.uptime + Math.random() * 0.01)
      }));
    }, 2000);
    
    // Update master node stats
    const masterInterval = setInterval(() => {
      setMasterNodeStats(prev => ({
        cpu: 10 + Math.floor(Math.random() * 5),
        memory: 3.5 + Math.random() * 0.5,
        load: 0.2 + Math.random() * 0.1
      }));
    }, 4000);
    
    // Update worker nodes
    const workersInterval = setInterval(() => {
      setWorkerNodes(prev => prev.map(worker => {
        let cpuChange = (Math.random() > 0.5 ? 1 : -1) * (Math.random() * 3);
        let memoryChange = (Math.random() > 0.5 ? 1 : -1) * (Math.random() * 0.2);
        let tasksChange = (Math.random() > 0.5 ? 1 : -1) * (Math.random() * 2);
        
        // Special handling for worker 3 which is under high load
        if (worker.id === 3) {
          cpuChange = Math.abs(cpuChange); // Always increase or stay the same
          if (worker.cpu > 85) {
            cpuChange = -Math.abs(cpuChange) / 2; // If too high, reduce slightly
          }
        }
        
        const newCpu = Math.min(Math.max(worker.cpu + cpuChange, 15), 90);
        const newMemory = Math.min(Math.max(worker.memory + memoryChange, 3.5), 8);
        const newTasks = Math.min(Math.max(Math.round(worker.tasks + tasksChange), 35), 95);
        
        let status = 'Healthy';
        if (newCpu > 70) status = 'High Load';
        
        return {
          ...worker,
          cpu: Math.round(newCpu),
          memory: parseFloat(newMemory.toFixed(1)),
          tasks: newTasks,
          status: status
        };
      }));
    }, 3500);
    
    // Simulating changing threat levels
    const pulseInterval = setInterval(() => {
      setPulseRate(prev => prev > 800 ? prev - 100 : 1000);
    }, 5000);
    
    // Clean up all intervals on component unmount
    return () => {
      clearInterval(threatInterval);
      clearInterval(nodeInterval);
      clearInterval(indexerInterval);
      clearInterval(masterInterval);
      clearInterval(workersInterval);
      clearInterval(pulseInterval);
    };
  }, []);

  // Random data for visualization
  const generateData = () => {
    return Array.from({ length: 48 }).map(() => Math.floor(15 + Math.random() * 85));
  };

  const [activityData] = useState(generateData());

  // Format large numbers with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <main className="container mx-auto px-3 sm:px-6 py-4 sm:py-8 relative">
      {/* HUD-Style Top Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-10 p-1 border border-red-900/30 rounded-md bg-black/30 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 sm:py-0 sm:gap-4 sm:px-4">
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">SYS:</span> ACTIVE
          </div>
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">PROT:</span> MAXIMAL
          </div>
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">SEC:</span> LEVEL 4
          </div>
        </div>
        <div className="flex items-center px-2 py-2 sm:py-0">
          <div className="h-2 bg-gradient-to-r from-red-900 via-red-600 to-red-900 animate-pulse mx-2 sm:mx-4" style={{ width: '60px', maxWidth: '25vw' }}></div>
          <div className="text-red-400 font-mono text-xs px-2 sm:px-4">
            <span className="text-slate-500">TIME:</span> {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Dashboard Header */}
      <div className="mb-8 sm:mb-12 relative">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <div className="relative">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1">
                AlertStrike <span className="text-red-500">SIEM & XDR Solution</span>
              </h1>
              <div className="absolute -top-2 -right-2 w-2 h-2 bg-red-600 rounded-full animate-ping"></div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-red-400 text-xs sm:text-base mb-4 sm:mb-6">
              <ShieldAlert className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Real-time Security</span>
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 hidden sm:block"></span>
              <span className="hidden sm:inline">Advanced Threat Detection</span>
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 hidden sm:block"></span>
              <span className="hidden sm:inline">AI-Powered Response</span>
            </div>
          </div>
          
          <div className="bg-black/40 rounded-md border border-red-900/30 p-3 sm:p-4 backdrop-blur-sm w-full sm:w-auto">
            <div className="flex items-center space-x-4">
              <div>
                <div className="text-xs sm:text-sm text-slate-400 mb-1">Global Threat Level</div>
                <div className="text-xl sm:text-2xl font-bold text-red-500">{threatStatus}</div>
              </div>
              <div className="relative h-14 w-14 sm:h-16 sm:w-16">
                <svg className="w-full h-full" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="16" fill="none" stroke="#27272a" strokeWidth="2"></circle>
                  <circle
                    cx="18" cy="18" r="16"
                    fill="none" stroke="#dc2626" strokeWidth="2"
                    strokeDasharray="100"
                    strokeDashoffset={100 - globalThreatLevel}
                    strokeLinecap="round"
                    transform="rotate(-90 18 18)">
                  </circle>
                </svg>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-lg sm:text-xl font-bold">{globalThreatLevel}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 1. AlertStrike Regional Portals */}
      <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-red-600 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-red-900/50 after:to-transparent after:ml-3">
        <Globe className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-600" />
        Demo AlertStrike MultiSites Portals
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10">
        {/* Pakistan Portal - Enhanced Card */}
        <a 
          href="https://demo-pk.alertstrike.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="group relative overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-br from-slate-950/90 to-black/95 backdrop-blur-sm transition-all duration-300 hover:border-red-700/50 hover:shadow-[0_0_40px_rgba(185,28,28,0.2)] hover:-translate-y-1"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 to-purple-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-900 via-red-600 to-red-900 opacity-30 group-hover:opacity-80 transition-opacity"></div>
          
          {/* Animated Corner Accent */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 right-0 h-10 w-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
          
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-black/70 rounded-lg border border-red-900/30 group-hover:border-red-700/60 transition-colors">
                  <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">AlertStrike OnShore</h3>
                  <p className="text-red-400 text-xs sm:text-sm">Demo OnShore Site Security Operations</p>
                </div>
              </div>
              <div className="relative">
                <span className="bg-green-900/30 text-green-400 text-xs px-2 sm:px-3 py-1 rounded-full border border-green-900/30 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-xs sm:text-sm">Operational Status</span>
                <span className="text-green-400 text-xs sm:text-sm font-mono">OPTIMAL</span>
              </div>
              <div className="w-full h-2 bg-slate-800/70 rounded-full overflow-hidden">
                <div className="h-full w-[98%] bg-gradient-to-r from-red-900 via-red-600 to-red-700 rounded-full relative">
                  <div className="absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-transparent via-red-300/30 to-transparent animate-pulse"></div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1 text-red-500" />
                  Alerts
                </p>
                <p className="text-base sm:text-xl font-bold">23</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Network className="h-3 w-3 mr-1 text-blue-500" />
                  Assets Capacity
                </p>
                <p className="text-base sm:text-xl font-bold">1000</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-3 w-3 mr-1" />
                  Score
                </p>
                <p className="text-base sm:text-xl font-bold">94<span className="text-xs sm:text-sm font-normal">/100</span></p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BarChart className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                <span className="text-xs sm:text-sm text-red-400 font-mono">Last: 00:03:21 ago</span>
              </div>
              <button className="flex items-center space-x-2 bg-red-900/40 hover:bg-red-800/60 backdrop-blur transition-all px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm border border-red-700/40 group-hover:border-red-600/70 group-hover:shadow-[0_0_15px_rgba(185,28,28,0.3)]">
                <span>Access Portal</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </a>
        
        {/* US Portal - Enhanced Card */}
        <a 
          href="https://demo-us.alertstrike.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="group relative overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-br from-slate-950/90 to-black/95 backdrop-blur-sm transition-all duration-300 hover:border-red-700/50 hover:shadow-[0_0_40px_rgba(185,28,28,0.2)] hover:-translate-y-1"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 to-purple-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-900 via-red-600 to-red-900 opacity-30 group-hover:opacity-80 transition-opacity"></div>
          
          {/* Animated Corner Accent */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 right-0 h-10 w-1.5 bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
          
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-black/70 rounded-lg border border-red-900/30 group-hover:border-red-700/60 transition-colors">
                  <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">AlertStrike OffShore</h3>
                  <p className="text-red-400 text-xs sm:text-sm">Demo OffShore Site Security Operations</p>
                </div>
              </div>
              <div className="relative">
                <span className="bg-green-900/30 text-green-400 text-xs px-2 sm:px-3 py-1 rounded-full border border-green-900/30 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-xs sm:text-sm">Operational Status</span>
                <span className="text-green-400 text-xs sm:text-sm font-mono">OPTIMAL</span>
              </div>
              <div className="w-full h-2 bg-slate-800/70 rounded-full overflow-hidden">
                <div className="h-full w-[99%] bg-gradient-to-r from-red-900 via-red-600 to-red-700 rounded-full relative">
                  <div className="absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-transparent via-red-300/30 to-transparent animate-pulse"></div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1 text-red-500" />
                  Alerts
                </p>
                <p className="text-base sm:text-xl font-bold">45</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Network className="h-3 w-3 mr-1 text-blue-500" />
                  Assets Capacity
                </p>
                <p className="text-base sm:text-xl font-bold">300</p>
              </div>
              <div className="bg-black/40 p-2 sm:p-3 rounded-lg border border-slate-800/80 group-hover:border-red-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-3 w-3 mr-1" />
                  Score
                </p>
                <p className="text-base sm:text-xl font-bold">97<span className="text-xs sm:text-sm font-normal">/100</span></p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BarChart className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                <span className="text-xs sm:text-sm text-red-400 font-mono">Last: 00:01:07 ago</span>
              </div>
              <button className="flex items-center space-x-2 bg-red-900/40 hover:bg-red-800/60 backdrop-blur transition-all px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm border border-red-700/40 group-hover:border-red-600/70 group-hover:shadow-[0_0_15px_rgba(185,28,28,0.3)]">
                <span>Access Portal</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </a>
      </div>

      {/* 2. AlertStrike Node Results */}
      <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-red-600 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-red-900/50 after:to-transparent after:ml-3">
        <Server className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-600" />
        Demo AlertStrike Node Results
      </h2>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-8 sm:mb-10">
        {nodeResults.map((node, index) => (
          <div key={node.id} className="bg-slate-950/70 border border-slate-800 rounded-lg p-3 sm:p-4 backdrop-blur-sm hover:border-red-900/50 transition-all shadow-lg shadow-black/40 group relative">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-900 to-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-xs sm:text-sm">Demo Node Result {node.id}</span>
              <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full flex items-center border border-green-900/30">
                <Zap className="h-3 w-3 mr-1" />
                {node.health}
              </span>
            </div>
            <div className="text-xl sm:text-3xl font-bold text-green-400">{node.performance}%</div>
            <div className="text-xs text-green-500 flex items-center mt-1">
              <span>{node.status}</span>
            </div>
            <div className="mt-3 h-1 w-full bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-green-700 to-green-500 rounded-full" style={{ width: `${node.performance}%` }}></div>
            </div>
          </div>
        ))}
      </div>

      {/* 3. Indexer Logs Status */}
      <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-red-600 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-red-900/50 after:to-transparent after:ml-3">
        <Activity className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-600" />
        Demo AlertStrike Indexer Logs Status
      </h2>
      
      <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-3 sm:p-4 backdrop-blur-sm hover:border-red-900/50 transition-all shadow-lg shadow-black/40 group relative mb-8 sm:mb-10">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-900 to-red-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-slate-400 text-xs sm:text-sm flex items-center">
            <Server className="h-3 w-3 sm:h-4 sm:w-4 mr-2 text-red-500" />
            Indexer Logs Status
          </span>
          <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full flex items-center border border-green-900/30">
            <Zap className="h-3 w-3 mr-1" />
            Healthy
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-3">
          <div className="bg-black/30 p-2 sm:p-3 rounded-lg border border-slate-800/80">
            <p className="text-slate-400 text-xs mb-1">Logs Processed</p>
            <p className="text-base sm:text-xl font-bold">{formatNumber(indexerStats.logsProcessed)}</p>
          </div>
          <div className="bg-black/30 p-2 sm:p-3 rounded-lg border border-slate-800/80">
            <p className="text-slate-400 text-xs mb-1">Indexing Rate</p>
            <p className="text-base sm:text-xl font-bold">{formatNumber(indexerStats.indexingRate)}/s</p>
          </div>
          <div className="bg-black/30 p-2 sm:p-3 rounded-lg border border-slate-800/80">
            <p className="text-slate-400 text-xs mb-1">Latency</p>
            <p className="text-base sm:text-xl font-bold">{indexerStats.latency}ms</p>
          </div>
          <div className="bg-black/30 p-2 sm:p-3 rounded-lg border border-slate-800/80">
            <p className="text-slate-400 text-xs mb-1">Uptime</p>
            <p className="text-base sm:text-xl font-bold">{indexerStats.uptime.toFixed(2)}%</p>
          </div>
        </div>
        <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
          <div className="h-full w-[99%] bg-gradient-to-r from-red-800 via-red-600 to-red-800 rounded-full relative">
            <div className="absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-transparent via-red-300/30 to-transparent animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* 4. AlertStrike Cluster Status */}
      <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-red-600 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-red-900/50 after:to-transparent after:ml-3">
        <Server className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-600" />
        Demo AlertStrike Cluster Status
      </h2>
      
      <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-3 sm:p-6 backdrop-blur-sm shadow-lg mb-8 sm:mb-10 relative">
        {/* Master Node */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-3">
            <div className="flex items-center">
              <div className="p-2 bg-red-900/30 rounded border border-red-900/40 mr-3">
                <Cpu className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-bold text-base sm:text-lg">Demo AlertStrike Master</h3>
                <div className="flex items-center text-xs sm:text-sm text-slate-400">
                  <span className="font-mono">Demo IP</span>
                  <span className="mx-2">•</span>
                  <span className="text-green-400 flex items-center">
                    <span className="h-1.5 w-1.5 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
                    Active
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <div className="bg-black/40 px-2 sm:px-3 py-1 sm:py-1.5 rounded border border-slate-800 text-xs sm:text-sm">
                <span className="text-slate-400">CPU:</span> <span className="text-red-400">{masterNodeStats.cpu}%</span>
              </div>
              <div className="bg-black/40 px-2 sm:px-3 py-1 sm:py-1.5 rounded border border-slate-800 text-xs sm:text-sm">
                <span className="text-slate-400">MEM:</span> <span className="text-red-400">{masterNodeStats.memory.toFixed(1)}GB</span>
              </div>
              <div className="bg-black/40 px-2 sm:px-3 py-1 sm:py-1.5 rounded border border-slate-800 text-xs sm:text-sm">
                <span className="text-slate-400">LOAD:</span> <span className="text-green-400">{masterNodeStats.load.toFixed(2)}</span>
              </div>
            </div>
          </div>
          
          <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full w-[93%] bg-gradient-to-r from-red-900 via-red-600 to-red-700"></div>
          </div>
        </div>
        
        {/* Divider */}
        <div className="relative mb-4 sm:mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="h-px w-full bg-slate-800"></div>
          </div>
          <div className="relative flex justify-center">
            <div className="bg-black px-3 text-xs text-slate-500">WORKER NODES</div>
          </div>
        </div>
        
        {/* Worker Nodes */}
        <div className="space-y-2 sm:space-y-4">
          {workerNodes.map((worker) => (
            <div key={worker.id} className={`bg-black/30 rounded-lg border ${worker.status === 'High Load' ? 'border-yellow-900/30' : 'border-slate-800'} p-2 sm:p-3 transition-colors hover:border-red-900/30`}>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div className="flex items-center">
                  <div className={`p-1.5 ${worker.status === 'High Load' ? 'bg-yellow-900/20' : 'bg-red-900/20'} rounded border ${worker.status === 'High Load' ? 'border-yellow-900/30' : 'border-red-900/30'} mr-3`}>
                    <Server className={`h-3 w-3 sm:h-4 sm:w-4 ${worker.status === 'High Load' ? 'text-yellow-500' : 'text-red-500'}`} />
                  </div>
                  <div>
                    <h4 className="font-medium text-sm sm:text-base">{worker.name}</h4>
                    <div className="flex items-center text-xs text-slate-400">
                      <span className="font-mono">{worker.ip}</span>
                      <span className="mx-2">•</span>
                      <span className={`${worker.status === 'High Load' ? 'text-yellow-400' : 'text-green-400'} flex items-center`}>
                        <span className={`h-1 w-1 ${worker.status === 'High Load' ? 'bg-yellow-500' : 'bg-green-500'} rounded-full mr-1 animate-pulse`}></span>
                        {worker.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 sm:gap-2">
                  <div className="bg-black/60 px-2 py-1 rounded border border-slate-800 text-xs">
                    <span className="text-slate-400">CPU:</span> <span className={`${worker.cpu > 70 ? 'text-yellow-400' : 'text-green-400'}`}>{worker.cpu}%</span>
                  </div>
                  <div className="bg-black/60 px-2 py-1 rounded border border-slate-800 text-xs">
                    <span className="text-slate-400">MEM:</span> <span className={`${worker.cpu > 70 ? 'text-yellow-400' : 'text-green-400'}`}>{worker.memory}GB</span>
                  </div>
                  <div className="bg-black/60 px-2 py-1 rounded border border-slate-800 text-xs">
                    <span className="text-slate-400">TASKS:</span> <span className={`${worker.cpu > 70 ? 'text-yellow-400' : 'text-green-400'}`}>{worker.tasks}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}

export default Dashboard;
